import React from 'react';
import { Link } from 'react-router-dom';
import Portlet from '@ui/portlet/Portlet';
import Tag from '@ui/tag/Tag';
import FormInputText from '@ui/form/input/FormInputText';
import FormButton from '@ui/button/FormButton';
import { Integration } from '@app/model/integration-settings/Integration';
import { formatDayTime, RESULT_TAG, TYPE_LABEL, TYPE_TEXT } from '../IntegrationSettings.utilities';
import IntegrationHeaderActions from './IntegrationHeaderActions';
import CredentialField from './CredentialField';
import useIntegrationCard from './useIntegrationCard';
import IntegrationRoomCatalogue from './IntegrationRoomCatalogue';
import { ProviderField } from './IntegrationFields';

const SUBMISSION_LOG_PATH = '/secured/rateshopper/rates/pricing-automation/price-submission-log';

// Pricing: the connection only (decision 3). The push timing stays in the Recommendation Submit and the Automatic
// Submit (ROO2-166). A push needs Recommendation submit (RoomPulse) and an Active Pricing Integration (decision 19).
const PricingCard: React.FunctionComponent<{ integration: Integration, onOpenLog: () => void }> = ({ integration, onOpenLog }) => {
	const card = useIntegrationCard(integration);
	const pricing = integration.pricing;
	const push = pricing?.lastPush ?? null;

	return <div id="pricing" className="scroll-mt-6">
		<Portlet isLarge title={TYPE_LABEL.PRICING}
						actions={<IntegrationHeaderActions integration={integration} onToggle={card.onToggle} isSaving={card.isBusy} />}>
			<p className="text-sm text-gray-500">{TYPE_TEXT.PRICING} </p>
			<form className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3" autoComplete="off">
				<ProviderField form={card.form} integration={integration} />
				<FormInputText form={card.form} name="username" label="WebHotelier username" isRequired disabled={integration.hotelCode !== null} />
				<CredentialField form={card.form} label="Hotel API Key" providerLabel="WebHotelier" credential={integration.credential}
												isReplacing={card.isReplacing} onReplace={card.setIsReplacing} />
			</form>
			<IntegrationRoomCatalogue integration={integration} isBusy={card.isBusy} isRefreshing={card.refreshRooms.isPending} isSharedRefreshing={card.catalogueRefreshing && !card.refreshRooms.isPending}
                isDirty={card.form.formState.isDirty} result={card.refreshRooms.data} error={card.refreshRooms.error}
                onRefresh={() => card.refreshRooms.mutate()} />
			<dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 border-t border-gray-200 pt-4 text-sm sm:grid-cols-4">
				<dt className="text-gray-500">Recommendation submit</dt>
				<dd className="text-gray-900 sm:col-span-3">{pricing?.recommendationSubmit === true ? 'On' : 'Off'} <span className="text-gray-500">(set by RoomPulse)</span></dd>
				<dt className="text-gray-500">Automatic Submit</dt>
				<dd className="text-gray-900 sm:col-span-3">
                    {pricing?.automaticSubmitMode === 'LIVE' ? 'Live' : 'Off'}
				</dd>
				<dt className="text-gray-500">Last price push</dt>
				<dd className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-900 sm:col-span-3">
					{push === null ? <span>No price push yet</span> : <>
						<span>{formatDayTime(push.createdAt)} · {push.type === 'AUTOMATIC' ? 'Automatic' : 'Manual'}</span>
						{push.prices !== null && <span>· {push.prices} prices</span>}
						{push.days !== null && <span>· {push.days} {push.days === 1 ? 'day' : 'days'}</span>}
						<Tag state={RESULT_TAG[push.state].state}>{RESULT_TAG[push.state].label}</Tag>
					</>}
					<Link to={SUBMISSION_LOG_PATH} className="font-semibold text-blue-600 hover:text-blue-500">Open the Price Submission Log →</Link>
				</dd>
			</dl>
			<div className="mt-6 flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4">
                {integration.status !== 'NOT_SET_UP' && <FormButton state="text" isLink onClick={onOpenLog}>View log →</FormButton>}
				<FormButton state="white" onClick={card.onTest} disabled={card.isBusy}>Test connection</FormButton>
				{(card.form.formState.isDirty || integration.status === 'NOT_SET_UP') &&
					<FormButton state="primary" onClick={card.onSave} disabled={card.isBusy}>Save</FormButton>}
			</div>
		</Portlet>
	</div>;
};

export default PricingCard;
